// Epoch 19: Lead Generator Enhancements - Tier Pricing Editor Component
// File: src/components/admin/TierPricingEditor.tsx
// Purpose: Editor for tier upcharge configuration with quality descriptions

import React from 'react';

interface TierPricingData {
  tier_upcharges: {
    [key: string]: number;
  };
}

interface TierPricingEditorProps {
  tierPricing: TierPricingData;
  onChange: (tierPricing: TierPricingData) => void;
}

const TIER_INFO = [
  {
    tier: '1',
    label: 'Tier 1 - Premium',
    description: 'Highest quality leads with verified contact info and recent activity',
    color: 'bg-green-100 border-green-200 text-green-800',
    iconColor: 'text-green-500'
  },
  {
    tier: '2',
    label: 'Tier 2 - High',
    description: 'High quality leads with strong profile data and good match likelihood',
    color: 'bg-blue-100 border-blue-200 text-blue-800',
    iconColor: 'text-blue-500'
  },
  {
    tier: '3',
    label: 'Tier 3 - Good',
    description: 'Good quality leads with complete profiles and moderate match likelihood',
    color: 'bg-yellow-100 border-yellow-200 text-yellow-800',
    iconColor: 'text-yellow-500'
  },
  {
    tier: '4',
    label: 'Tier 4 - Standard',
    description: 'Standard quality leads with basic profile data, lower cost option',
    color: 'bg-orange-100 border-orange-200 text-orange-800',
    iconColor: 'text-orange-500'
  },
  {
    tier: '5',
    label: 'Tier 5 - Economy',
    description: 'Economy quality leads with minimal data, budget-friendly option',
    color: 'bg-gray-100 border-gray-200 text-gray-800',
    iconColor: 'text-gray-500'
  }
];

export default function TierPricingEditor({ tierPricing, onChange }: TierPricingEditorProps) {
  const handleTierUpchargeChange = (tier: string, value: number) => {
    onChange({
      ...tierPricing,
      tier_upcharges: {
        ...tierPricing.tier_upcharges,
        [tier]: value
      }
    });
  };

  const getTotalUpchargeForLeads = (leadCount: number) => {
    // Simulate distribution for cost preview
    const distribution = { '1': 0.1, '2': 0.15, '3': 0.3, '4': 0.25, '5': 0.2 };
    let total = 0;
    
    Object.entries(distribution).forEach(([tier, percentage]) => {
      const tierLeadCount = leadCount * percentage;
      const upcharge = tierPricing.tier_upcharges[tier] || 0;
      total += tierLeadCount * upcharge;
    });
    
    return total;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Tier Pricing Configuration</h3>
        <p className="text-sm text-gray-600">
          Configure upcharge amounts for each lead quality tier. Higher tiers command premium pricing.
        </p>
      </div>

      {/* Tier Configuration Cards */}
      <div className="space-y-4">
        {TIER_INFO.map((tierInfo) => {
          const currentUpcharge = tierPricing.tier_upcharges[tierInfo.tier] || 0;
          
          return (
            <div
              key={tierInfo.tier}
              className={`border rounded-lg p-4 ${tierInfo.color}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <div className={`w-3 h-3 rounded-full mr-3 ${tierInfo.iconColor.replace('text-', 'bg-')}`}></div>
                    <h4 className="font-medium">{tierInfo.label}</h4>
                  </div>
                  <p className="text-sm opacity-80 mb-3">{tierInfo.description}</p>
                  
                  {/* Upcharge Input */}
                  <div className="flex items-center space-x-3">
                    <label className="text-sm font-medium">Upcharge per lead:</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                        <span className="text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        value={currentUpcharge}
                        onChange={(e) => handleTierUpchargeChange(tierInfo.tier, parseFloat(e.target.value) || 0)}
                        className="w-20 pl-6 pr-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        step="0.001"
                        min="0"
                        max="1"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Preview Calculations */}
                <div className="text-right text-sm">
                  <div className="font-medium">Cost Examples:</div>
                  <div className="space-y-1 mt-1">
                    <div>100 leads: {formatCurrency(currentUpcharge * 100)}</div>
                    <div>500 leads: {formatCurrency(currentUpcharge * 500)}</div>
                    <div>1K leads: {formatCurrency(currentUpcharge * 1000)}</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pricing Impact Preview */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-3">Tier Upcharge Impact Preview</h4>
        <p className="text-xs text-blue-700 mb-3">
          Based on typical tier distribution (T1: 10%, T2: 15%, T3: 30%, T4: 25%, T5: 20%)
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="bg-white rounded p-3">
            <div className="text-blue-700 font-medium">100 Leads</div>
            <div className="text-blue-600">Tier upcharge: {formatCurrency(getTotalUpchargeForLeads(100))}</div>
          </div>
          <div className="bg-white rounded p-3">
            <div className="text-blue-700 font-medium">500 Leads</div>
            <div className="text-blue-600">Tier upcharge: {formatCurrency(getTotalUpchargeForLeads(500))}</div>
          </div>
          <div className="bg-white rounded p-3">
            <div className="text-blue-700 font-medium">1,000 Leads</div>
            <div className="text-blue-600">Tier upcharge: {formatCurrency(getTotalUpchargeForLeads(1000))}</div>
          </div>
        </div>
      </div>

      {/* Validation Warnings */}
      <div className="space-y-2">
        {Object.entries(tierPricing.tier_upcharges).some(([, upcharge]) => upcharge < 0) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-700 text-sm">All tier upcharges must be $0.00 or greater</span>
            </div>
          </div>
        )}

        {tierPricing.tier_upcharges['1'] <= tierPricing.tier_upcharges['5'] && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="text-yellow-700 text-sm">
                Tier 1 (premium) should typically have higher upcharge than Tier 5 (economy)
              </span>
            </div>
          </div>
        )}

        {Object.values(tierPricing.tier_upcharges).some(upcharge => upcharge > 0.5) && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="text-yellow-700 text-sm">
                High upcharges (&gt;$0.50) may reduce conversion rates
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Best Practices */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">💡 Tier Pricing Best Practices</h4>
        <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside">
          <li>Higher tiers should have progressively higher upcharges (T1 &gt; T2 &gt; T3 &gt; T4 &gt; T5)</li>
          <li>Keep upcharges reasonable - typically $0.01-$0.15 per lead</li>
          <li>Tier 1 premium leads can command 5-10x the upcharge of Tier 5</li>
          <li>Monitor tier distribution to ensure pricing aligns with actual quality</li>
          <li>Consider market willingness to pay premium for higher quality</li>
          <li>Test different upcharge structures with A/B testing</li>
        </ul>
      </div>
    </div>
  );
}